import ButtonIconT from '@components/tailwind-components/Button/ButtonIconT';
import CheckboxInputT from '@components/tailwind-components/Input/CheckboxInputT';
import {
  type Control,
  type FieldErrors,
  type UseFormClearErrors,
  type UseFormSetValue,
  type UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { generate } from 'short-uuid';
import MaterialIcon from '../../../../common/MaterialIcon';
import type { FormDataIntervals } from '../../Calendar.types';
import TargetSelector from './TargetSelector';
import { TimeIntervalInput } from './TimeIntervalInput';
import TypeSelector from './TypeSelector';

type Props = {
  name: 'newAvailabilities';
  control: Control<FormDataIntervals>;
  errors: FieldErrors<FormDataIntervals>;
  clearErrors: UseFormClearErrors<FormDataIntervals>;
  watch: UseFormWatch<FormDataIntervals>;
  setValue: UseFormSetValue<FormDataIntervals>;
};

export const IntervalInputField: React.FC<Props> = ({
  errors,
  clearErrors,
  control,
  name,
  watch,
}) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  return (
    <div>
      {fields.map((field, actionId) => {
        const fromErrorMessage =
          errors?.newAvailabilities?.[actionId]?.from?.message?.toString() ??
          '';
        const toErrorMessage =
          errors?.newAvailabilities?.[actionId]?.to?.message?.toString() ?? '';

        const targetType = watch(`${name}.${actionId}.type`);

        return (
          <div key={field.id} className='mb-1 border-b border-gray-200'>
            <div className='flex flex-start items-center gap-x-5 gap-y-2 flex-wrap md:flex-nowrap'>
              <TimeIntervalInput
                nameFrom={`${name}.${actionId}.from`}
                nameTo={`${name}.${actionId}.to`}
                disabled={field.recurring}
                control={control}
              />
              <TypeSelector
                name={`${name}.${actionId}.type`}
                disabled={field.recurring}
                control={control}
              />
              <div className='flex gap-x-5 items-center flex-grow'>
                <TargetSelector
                  type={targetType || 'agent'}
                  name={`${name}.${actionId}.targetId`}
                  control={control}
                  disabled={field.recurring}
                />
                {field.recurring === false && (
                  <div
                    className='cursor-pointer text-danger flex items-center mt-4'
                    onClick={() => remove(actionId)}>
                    <MaterialIcon icon='delete' />
                  </div>
                )}
                {field.recurring && (
                  <div className='mt-4'>
                    <CheckboxInputT
                      label={t('handover-settings.interval.include')}
                      field={`${name}.${actionId}.include`}
                      control={control}
                      id={`${name}${actionId}checkbox`}
                    />
                  </div>
                )}
              </div>
            </div>
            {(fromErrorMessage || toErrorMessage) && (
              <span className='flex text-danger text-xs mb-2'>
                • {fromErrorMessage || toErrorMessage || ''}
              </span>
            )}
          </div>
        );
      })}
      <div className='flex gap-3'>
        <ButtonIconT
          className='my-3'
          icon='add_circle'
          onClick={() => {
            append({
              to: '',
              from: '',
              recurring: false,
              id: generate(),
              type: 'agent',
            } as never);
            clearErrors();
          }}
          label={t('handover-settings.interval.add')}
          color='primary-base'
        />
      </div>
    </div>
  );
};

export default IntervalInputField;
